import { useState, useEffect } from 'react';
import { Clock, Pause, Play, Square, Minimize2, Maximize2 } from 'lucide-react';
import { financeV2Service } from '../services/financeV2Service';
import { ActiveTimer } from '../types/financeV2.types';
import { parseDbTimestamp } from '../utils/datetime';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

export function GlobalTimerWidget() {
  const [timer, setTimer] = useState<ActiveTimer | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [isMinimized, setIsMinimized] = useState(false);
  const [showStopModal, setShowStopModal] = useState(false);
  const [stopNotes, setStopNotes] = useState('');

  // Poll for active timer
  useEffect(() => {
    loadActiveTimer();
    const interval = setInterval(loadActiveTimer, 10000); // Check every 10 seconds
    return () => clearInterval(interval);
  }, []);

  // Update elapsed time every second
  useEffect(() => {
    if (!timer || timer.is_paused) return;

    const interval = setInterval(() => {
      const startTime = parseDbTimestamp(timer.start_time);
      if (!startTime) return;
      const now = new Date();
      const totalElapsed = Math.floor((now.getTime() - startTime.getTime()) / 1000);
      const netElapsed = totalElapsed - (timer.total_paused_duration || 0);
      setElapsedSeconds(Math.max(0, netElapsed));
    }, 1000);

    return () => clearInterval(interval);
  }, [timer]);

  const loadActiveTimer = async () => {
    try {
      const response = await financeV2Service.timer.getActive();
      if (response.success && response.data.timer) {
        setTimer(response.data.timer);

        // Calculate initial elapsed time
        if (response.data.timer) {
          const startTime = parseDbTimestamp(response.data.timer.start_time);
          if (startTime) {
            const now = new Date();
            const totalElapsed = Math.floor((now.getTime() - startTime.getTime()) / 1000);
            const netElapsed = totalElapsed - (response.data.timer.total_paused_duration || 0);
            setElapsedSeconds(Math.max(0, netElapsed));
          }
        }
      } else {
        setTimer(null);
        setElapsedSeconds(0);
      }
    } catch (error) {
      console.error('Error loading active timer:', error);
    }
  };

  const handlePause = async () => {
    try {
      const response = await financeV2Service.timer.pause();
      if (response.success) {
        await loadActiveTimer();
      }
    } catch (error) {
      console.error('Error pausing timer:', error);
      alert('Erro ao pausar cronômetro');
    }
  };

  const handleResume = async () => {
    try {
      const response = await financeV2Service.timer.resume();
      if (response.success) {
        await loadActiveTimer();
      }
    } catch (error) {
      console.error('Error resuming timer:', error);
      alert('Erro ao retomar cronômetro');
    }
  };

  const handleStop = async () => {
    try {
      const response = await financeV2Service.timer.stop({
        notes: stopNotes || undefined
      });

      if (response.success) {
        setShowStopModal(false);
        setStopNotes('');
        setTimer(null);
        setElapsedSeconds(0);
        alert('Registro de horas criado com sucesso!');
      }
    } catch (error) {
      console.error('Error stopping timer:', error);
      alert('Erro ao parar cronômetro');
    }
  };

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const calculateCurrentValue = () => {
    if (!timer || !timer.service_type_rate) return 0;
    const hours = elapsedSeconds / 3600;
    return hours * timer.service_type_rate;
  };

  // Don't show widget if no timer is active
  if (!timer) return null;

  return (
    <>
      <div
        className={`fixed right-4 sm:right-6 bottom-4 sm:bottom-6 left-4 sm:left-auto z-50 overflow-hidden rounded-xl border bg-card shadow-lg ring-1 ring-primary/30 transition-all duration-300 ${
          isMinimized ? 'sm:w-64' : 'sm:w-80'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between bg-primary px-4 py-2.5 text-primary-foreground">
          <div className="flex items-center gap-2">
            <Clock className={`size-4 ${timer.is_paused ? '' : 'animate-pulse'}`} aria-hidden="true" />
            <span className="text-sm font-semibold">
              {timer.is_paused ? 'Pausado' : 'Em Andamento'}
            </span>
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => setIsMinimized(!isMinimized)}
                className="rounded-sm p-1 transition-colors hover:bg-primary-foreground/15"
                aria-label={isMinimized ? 'Expandir' : 'Minimizar'}
              >
                {isMinimized ? <Maximize2 className="size-4" /> : <Minimize2 className="size-4" />}
              </button>
            </TooltipTrigger>
            <TooltipContent>{isMinimized ? 'Expandir' : 'Minimizar'}</TooltipContent>
          </Tooltip>
        </div>

        {!isMinimized && (
          <div className="p-4">
            {/* Timer Display */}
            <div className="mb-4 text-center">
              <div className="mb-2 font-mono text-4xl font-bold tabular-nums text-foreground">
                {formatTime(elapsedSeconds)}
              </div>
              <div className="text-sm text-muted-foreground">
                Valor: {formatCurrency(calculateCurrentValue())}
              </div>
            </div>

            {/* Service Info */}
            <div className="mb-4 rounded-lg bg-muted p-3">
              <div className="text-sm">
                <div className="mb-1 font-medium text-foreground">
                  {timer.service_type_name}
                </div>
                {timer.client_name && (
                  <div className="text-muted-foreground">
                    Cliente: {timer.client_name}
                  </div>
                )}
                {timer.case_number && (
                  <div className="text-muted-foreground">
                    Caso: {timer.case_number}
                  </div>
                )}
                {timer.description && (
                  <div className="mt-1 text-xs text-muted-foreground">
                    {timer.description}
                  </div>
                )}
              </div>
            </div>

            {/* Controls */}
            <div className="flex gap-2">
              {timer.is_paused ? (
                <Button onClick={handleResume} className="flex-1">
                  <Play className="size-4" />
                  Retomar
                </Button>
              ) : (
                <Button variant="secondary" onClick={handlePause} className="flex-1">
                  <Pause className="size-4" />
                  Pausar
                </Button>
              )}

              <Button variant="destructive" onClick={() => setShowStopModal(true)} className="flex-1">
                <Square className="size-4" />
                Parar
              </Button>
            </div>
          </div>
        )}

        {isMinimized && (
          <div className="p-3">
            <div className="flex items-center justify-between">
              <div className="font-mono text-2xl font-bold tabular-nums text-foreground">
                {formatTime(elapsedSeconds)}
              </div>
              <div className="flex gap-1">
                {timer.is_paused ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button size="icon" onClick={handleResume} aria-label="Retomar">
                        <Play className="size-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Retomar</TooltipContent>
                  </Tooltip>
                ) : (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button size="icon" variant="secondary" onClick={handlePause} aria-label="Pausar">
                        <Pause className="size-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Pausar</TooltipContent>
                  </Tooltip>
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button size="icon" variant="destructive" onClick={() => setShowStopModal(true)} aria-label="Parar">
                      <Square className="size-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Parar</TooltipContent>
                </Tooltip>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Stop Modal */}
      <Dialog
        open={showStopModal}
        onOpenChange={(open) => {
          setShowStopModal(open);
          if (!open) setStopNotes('');
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Parar Cronômetro</DialogTitle>
          </DialogHeader>

          <div className="rounded-lg border bg-muted/40 p-3 text-sm">
            <div className="mb-1 font-medium">Tempo Total</div>
            <div className="font-mono text-2xl font-bold tabular-nums">{formatTime(elapsedSeconds)}</div>
            <div className="mt-2 text-muted-foreground">
              Valor: <span className="font-medium text-foreground">{formatCurrency(calculateCurrentValue())}</span>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="timer-stop-notes">Observações finais (opcional)</Label>
            <Textarea
              id="timer-stop-notes"
              value={stopNotes}
              onChange={(e) => setStopNotes(e.target.value)}
              rows={3}
              placeholder="Adicione observações sobre o trabalho realizado..."
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowStopModal(false);
                setStopNotes('');
              }}
            >
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleStop}>
              Parar e Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
